import { Box, FormControl, InputLabel, Select, MenuItem, Chip, Stack, IconButton, Slider, Typography, TextField, Skeleton, InputAdornment } from '@mui/material';
import { Cancel } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import 'chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm';
import Spinner from './Spinner';
import Error from './Error';
import { useGetAnalysisTimeframeQuery, useGetVisitsQuery } from '../features/api/rest/apiStatsSlice';
import { Chart, LinearScale, PointElement, Tooltip, Legend, TimeScale } from 'chart.js';
Chart.register(LinearScale, PointElement, Tooltip, Legend, TimeScale);
dayjs.extend(isBetween);

function Trends() {
  const { ean } = useParams();
  const { t } = useTranslation();
  const chartRef = useRef();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTerms, setSelectedTerms] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [months, setMonths] = useState(1);
  const [precision, setPrecision] = useState(1);
  const [unit, setUnit] = useState('day');
  const [colors, setColors] = useState({});
  const { data: timeframe, isLoading: isLoadingTimeframe, isFetching: isFetchingTimeframe, isError: isErrorTimeframe } = useGetAnalysisTimeframeQuery();
  const { visits, isLoading, isFetching, isError } = useGetVisitsQuery(
    { eans: [ean], ordering: 'timestamp', psize: 50000 },
    {
      selectFromResult: ({ data }) => ({
        visits: data?.results,
      }),
    }
  );
  const terms = (visits?.map((term) => term.search_term_text) || []).filter((value, index, self) => self.indexOf(value) === index).sort();

  useEffect(() => {
    function aggregateData(data) {
      const counts = data.reduce((acc, visit) => {
        acc[visit.x] = (acc[visit.x] || 0) + visit.y;
        return acc;
      }, {});
      return Object.keys(counts).map((key) => {
        return { x: parseInt(key), y: counts[key] };
      });
    }
    function round(timestamp, seconds) {
      return Math.floor(timestamp / (seconds * 1000)) * (seconds * 1000);
    }
    setDatasets(
      selectedTerms.map((term) => {
        const data = visits
          .filter((visit) => visit.search_term_text === term)
          .filter((visit) => dayjs(visit.timestamp).isBetween(dayjs(timeframe?.start_analysis_time).subtract(months, 'month'), dayjs(timeframe?.start_analysis_time).add(timeframe?.analysis_window_duration, 'second')))
          .map((visit) => {
            return {
              x: round(dayjs(visit.timestamp).valueOf(), precision * 60 * 60),
              y: visit.count,
            };
          });
        return {
          label: term,
          data: aggregateData(data),
          fill: true,
        };
      })
    );
  }, [selectedTerms, visits, months, precision]);

  useEffect(() => {
    const x = chartRef.current?.scales['x'];
    const diffDays = dayjs(x?.getValueForPixel(x.right)).diff(dayjs(x?.getValueForPixel(x.left)), 'day');
    setUnit(diffDays > 15 ? 'month' : 'day');

    const colors = {};
    chartRef.current?.data.datasets.forEach((dataset) => {
      colors[dataset.label] = dataset.borderColor;
    });
    setColors(colors);
  }, [datasets]);

  useEffect(() => {
    setPrecision(timeframe?.analysis_window_duration / 60 / 60);
  }, [timeframe]);

  useEffect(() => {
    setSelectedTerms(
      decodeURI(searchParams.get('terms'))
        .split(',')
        .filter((term) => terms.includes(term))
    );
  }, [searchParams, visits]);

  const handleChange = (e) => {
    searchParams.set('terms', encodeURI(e.target.value.toString()));
    if (!searchParams.get('terms')) searchParams.delete('terms');
    setSearchParams(searchParams);
  };

  const handleDelete = (value) => {
    searchParams.set('terms', encodeURI(selectedTerms.filter((selectedTerm) => selectedTerm !== value).toString()));
    if (!searchParams.get('terms')) searchParams.delete('terms');
    setSearchParams(searchParams);
  };

  const handlePrecision = (e) => {
    if (e.target.value >= 24) setPrecision(24);
    else if (e.target.value <= timeframe?.analysis_window_duration / 60 / 60) setPrecision(timeframe?.analysis_window_duration / 60 / 60);
    else setPrecision(e.target.value);
  };

  if (isLoading || isFetching) return <Spinner />;

  if (isError) return <Error />;

  return (
    <Stack direction="row" flexWrap="wrap" justifyContent="space-around">
      <Stack direction="column">
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('organizations.product.charts.monthsDisplayed')}: {months}
        </Typography>
        <Slider
          sx={{ mt: 1, ml: 1, width: '95%' }}
          value={months}
          onChange={(e, value) => setMonths(value)}
          min={1}
          max={12}
          step={1}
          valueLabelDisplay="off"
          marks={[
            { value: 1, label: '1' },
            { value: 12, label: '12' },
          ]}
        />
        <FormControl sx={{ mt: 2, width: 300 }}>
          <InputLabel>{t('organizations.product.charts.searchTerms')}</InputLabel>
          <Select
            multiple
            label={t('organizations.product.charts.searchTerms')}
            value={selectedTerms}
            onChange={handleChange}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={value}
                    sx={{ backgroundColor: colors[value] || undefined }}
                    onDelete={() => handleDelete(value)}
                    deleteIcon={
                      <IconButton
                        sx={{ p: 0 }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <Cancel />
                      </IconButton>
                    }
                  />
                ))}
              </Box>
            )}
          >
            {terms.map((term) => (
              <MenuItem key={term} value={term}>
                {term}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isLoadingTimeframe || isFetchingTimeframe ? (
          <Skeleton />
        ) : isErrorTimeframe ? (
          <Error />
        ) : (
          <TextField
            sx={{ mt: 2 }}
            variant="filled"
            type="number"
            value={precision}
            onChange={handlePrecision}
            label={t('organizations.product.charts.precision')}
            InputProps={{ max: 24, endAdornment: <InputAdornment position="start">{t('organizations.product.charts.hours')}</InputAdornment> }}
          >
            {t('organizations.product.charts.precision')}
          </TextField>
        )}
      </Stack>
      <Box minWidth="70%">
        <Line
          data={{ datasets }}
          options={{
            scales: {
              x: {
                type: 'time',
                time: {
                  unit: unit,
                  displayFormats: {
                    day: 'DD/MM/YYYY',
                  },
                  tooltipFormat: 'dddd, DD MMMM YYYY, HH:mm',
                  ticks: {
                    source: 'data',
                  },
                },
              },
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: t('organizations.product.charts.searches'),
                },
              },
            },
            plugins: {
              title: {
                display: true,
                text: t('organizations.product.charts.searchTerms'),
              },
              colors: {
                forceOverride: true,
              },
            },
          }}
          ref={chartRef}
        />
      </Box>
    </Stack>
  );
}

export default Trends;
